// Stammdaten "Startguthabenarten": Definiert die Einheiten/Arten fuer das
// Startguthaben eines Tarifs (z. B. EUR, Minuten, TOKEN).
// Die hier gepflegten Eintraege stehen im Tarif-Dialog (Reiter "Allgemein")
// in der 1:n-Gruppe "Startguthaben" dynamisch zur Auswahl.

import React, { useState, useEffect, useCallback } from "react";
import UserService from "../services/user.service";
import EventBus from "../common/EventBus";
import { Table, Button, Container, Modal, Form, ButtonGroup, Pagination, Spinner, Badge } from "react-bootstrap";
import { FaEdit, FaTrash } from "react-icons/fa";
import ErrorModal from "./ErrorModal";

const emptyForm = {
  credit_type_name: "",
  credit_type_code: "",
  unit_symbol: "",
  is_active: true,
  sort_order: "",
};

const normalize = (row = {}) => ({
  ...emptyForm,
  credit_type_name: row.credit_type_name || "",
  credit_type_code: row.credit_type_code || "",
  unit_symbol: row.unit_symbol || "",
  is_active: !(row.is_active === false || row.is_active === 0),
  sort_order: row.sort_order === null || row.sort_order === undefined ? "" : `${row.sort_order}`,
});

const formatDate = (value) => {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
};

const BoardStartCreditTypes = () => {
  const [creditTypes, setCreditTypes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reloadSignal, setReloadSignal] = useState(false);
  const [errors, setErrors] = useState({});
  const [apierrors, setApiErrors] = useState("");
  const [errorCode, setApiErrorCode] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "sort_order", direction: "asc" });
  const [filterTerm, setFilterTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Lade, bitte warten...");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [formData, setFormData] = useState({ ...emptyForm });

  const showApiError = useCallback((error, fallback) => {
    const serverMessage =
      (error && error.response && error.response.data && error.response.data.message) ||
      (error && error.message) ||
      fallback;
    setApiErrors(serverMessage);
    setApiErrorCode(error && error.response ? `HTTP ${error.response.status}` : "");
    setShowModal(true);
    if (error && error.response && error.response.status === 401) {
      EventBus.dispatch("logout");
    }
  }, []);

  useEffect(() => {
    let active = true;
    UserService.getStartCreditTypesBoard()
      .then((response) => {
        if (!active) return;
        setCreditTypes(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        if (!active) return;
        setCreditTypes([]);
        showApiError(error, "Die Startguthabenarten konnten nicht geladen werden.");
      });
    return () => {
      active = false;
    };
  }, [reloadSignal, showApiError]);

  const validateForm = (data) => {
    const newErrors = {};
    if (!data.credit_type_name || !data.credit_type_name.trim()) {
      newErrors.credit_type_name = "Bitte eine Bezeichnung für die Startguthabenart angeben.";
    } else if (data.credit_type_name.trim().length > 100) {
      newErrors.credit_type_name = "Die Bezeichnung darf höchstens 100 Zeichen lang sein.";
    }
    const code = (data.credit_type_code || "").trim();
    if (code && !/^[A-Za-z0-9_]{1,32}$/.test(code)) {
      newErrors.credit_type_code = "Nur Buchstaben, Ziffern und Unterstriche, höchstens 32 Zeichen.";
    }
    const sym = (data.unit_symbol || "").trim();
    if (sym && sym.length > 32) {
      newErrors.unit_symbol = "Das Einheitssymbol darf höchstens 32 Zeichen lang sein.";
    }
    const order = `${data.sort_order ?? ""}`.trim();
    if (order && !/^\d{1,6}$/.test(order)) {
      newErrors.sort_order = "Bitte eine ganze Zahl angeben.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const filtered = Array.isArray(creditTypes)
    ? creditTypes.filter((row) => {
        const term = filterTerm.trim().toLowerCase();
        if (!term) return true;
        return (
          (row.credit_type_name || "").toLowerCase().includes(term) ||
          (row.credit_type_code || "").toLowerCase().includes(term) ||
          (row.unit_symbol || "").toLowerCase().includes(term) ||
          `${row.credit_type_id}` === term
        );
      })
    : [];

  const sorted = [...filtered].sort((a, b) => {
    const key = sortConfig.key;
    const av = a[key] === null || a[key] === undefined ? "" : a[key];
    const bv = b[key] === null || b[key] === undefined ? "" : b[key];
    if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
    if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const currentRows = sorted.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleCreateClick = () => {
    setApiErrors("");
    setApiErrorCode("");
    setErrors({});
    setFormData({ ...emptyForm });
    setShowCreateModal(true);
  };

  const handleEditClick = (row) => {
    setApiErrors("");
    setApiErrorCode("");
    setErrors({});
    setSelected(row);
    setFormData(normalize(row));
    setShowEditModal(true);
  };

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const toPayload = (data) => ({
    credit_type_name: (data.credit_type_name || "").trim(),
    credit_type_code: (data.credit_type_code || "").trim().toUpperCase() || null,
    unit_symbol: (data.unit_symbol || "").trim() || null,
    is_active: !!data.is_active,
    sort_order: `${data.sort_order ?? ""}`.trim() === "" ? null : Number(data.sort_order),
  });

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm(formData)) return;
    try {
      setLoadingText("Startguthabenart wird gespeichert, bitte warten...");
      setLoading(true);
      await UserService.createStartCreditTypeBoard(toPayload(formData));
      setShowCreateModal(false);
      setReloadSignal((prev) => !prev);
    } catch (error) {
      showApiError(error, "Die Startguthabenart konnte nicht angelegt werden.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm(formData)) return;
    try {
      setLoadingText("Startguthabenart wird aktualisiert, bitte warten...");
      setLoading(true);
      await UserService.updateStartCreditTypeBoard(selected.credit_type_id, toPayload(formData));
      setShowEditModal(false);
      setReloadSignal((prev) => !prev);
    } catch (error) {
      showApiError(error, "Die Startguthabenart konnte nicht gespeichert werden.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (row, e) => {
    e.preventDefault();
    const confirmed = window.confirm(
      `Soll die Startguthabenart „${row.credit_type_name}“ wirklich gelöscht werden?`
    );
    if (!confirmed) return;
    try {
      setLoadingText("Startguthabenart wird gelöscht, bitte warten...");
      setLoading(true);
      await UserService.deleteStartCreditTypeBoard(row.credit_type_id);
      setReloadSignal((prev) => !prev);
    } catch (error) {
      showApiError(error, "Die Startguthabenart konnte nicht gelöscht werden.");
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => (
    <>
      <Form.Group controlId="creditTypeName">
        <Form.Label>Bezeichnung *</Form.Label>
        <Form.Control
          type="text"
          placeholder="z. B. EUR, Minuten, TOKEN"
          name="credit_type_name"
          value={formData.credit_type_name}
          onChange={handleChange}
          isInvalid={!!errors.credit_type_name}
          maxLength={100}
        />
        <Form.Control.Feedback type="invalid">{errors.credit_type_name}</Form.Control.Feedback>
        <Form.Text muted>Dieser Name erscheint in der Tarifverwaltung unter „Startguthabenart“.</Form.Text>
      </Form.Group>

      <Form.Group controlId="creditTypeCode" className="mt-3">
        <Form.Label>Technischer Schlüssel (optional)</Form.Label>
        <Form.Control
          type="text"
          placeholder="z. B. EUR, MINUTES, TOKEN"
          name="credit_type_code"
          value={formData.credit_type_code}
          onChange={handleChange}
          isInvalid={!!errors.credit_type_code}
          maxLength={32}
        />
        <Form.Control.Feedback type="invalid">{errors.credit_type_code}</Form.Control.Feedback>
        <Form.Text muted>
          Stabiler Bezugspunkt für Schnittstellen (nur Großbuchstaben, Ziffern und Unterstriche).
        </Form.Text>
      </Form.Group>

      <Form.Group controlId="creditTypeUnitSymbol" className="mt-3">
        <Form.Label>Einheit / Symbol (optional)</Form.Label>
        <Form.Control
          type="text"
          placeholder="z. B. €, Min., TOKEN"
          name="unit_symbol"
          value={formData.unit_symbol}
          onChange={handleChange}
          isInvalid={!!errors.unit_symbol}
          maxLength={32}
        />
        <Form.Control.Feedback type="invalid">{errors.unit_symbol}</Form.Control.Feedback>
        <Form.Text muted>Kurzes Einheitssymbol für die Anzeige hinter dem Betrag/Wert.</Form.Text>
      </Form.Group>

      <Form.Group controlId="creditTypeSortOrder" className="mt-3">
        <Form.Label>Reihenfolge (optional)</Form.Label>
        <Form.Control
          type="number"
          min="0"
          step="10"
          placeholder="z. B. 10"
          name="sort_order"
          value={formData.sort_order}
          onChange={handleChange}
          isInvalid={!!errors.sort_order}
        />
        <Form.Control.Feedback type="invalid">{errors.sort_order}</Form.Control.Feedback>
        <Form.Text muted>Kleinere Zahlen erscheinen in Auswahllisten weiter oben.</Form.Text>
      </Form.Group>

      <Form.Group controlId="creditTypeIsActive" className="mt-3">
        <Form.Check
          type="checkbox"
          name="is_active"
          checked={!!formData.is_active}
          onChange={handleChange}
          label="Aktiv (zur Auswahl in Tarifen freigegeben)"
        />
        <Form.Text muted>
          Inaktive Startguthabenarten bleiben bei bestehenden Tarifen erhalten, können aber nicht neu gewählt werden.
        </Form.Text>
      </Form.Group>
    </>
  );

  return (
    <Container>
      <h1 className="mt-5">Startguthabenarten</h1>
      <p className="text-muted">
        Dynamische Verwaltung der Startguthabenarten (z. B. EUR, Minuten, TOKEN).
        Diese Arten stehen im Tarifmodul unter dem Reiter „Allgemein“ als 1:n-Gruppe „Startguthaben“ zur Auswahl.
      </p>

      <ErrorModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        errorCode={errorCode}
        errorMessage={apierrors}
      />

      <Button variant="info" onClick={handleCreateClick} className="mr-2 mb-2">
        Erstellen
      </Button>

      {loading && (
        <div className="spinner-overlay">
          <div className="spinner-background">
            <Spinner animation="border" role="status" className="spinner-border">
              <span className="sr-only">Lade...</span>
            </Spinner>
            <div className="spinner-text">...{loadingText}</div>
          </div>
        </div>
      )}

      <Form.Group controlId="startCreditTypeSearch">
        <Form.Control
          type="text"
          placeholder="Suche nach Bezeichnung, Schlüssel, Symbol oder ID"
          value={filterTerm}
          onChange={(e) => {
            setFilterTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
      </Form.Group>

      <div className="table-responsive">
        <Table striped bordered hover className="mt-4">
          <thead>
            <tr>
              <th onClick={() => handleSort("credit_type_id")} style={{ cursor: "pointer" }}>
                ID {sortConfig.key === "credit_type_id" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("credit_type_name")} style={{ cursor: "pointer" }}>
                Bezeichnung {sortConfig.key === "credit_type_name" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("credit_type_code")} style={{ cursor: "pointer" }}>
                Schlüssel {sortConfig.key === "credit_type_code" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th>Einheit / Symbol</th>
              <th>Status</th>
              <th onClick={() => handleSort("sort_order")} style={{ cursor: "pointer" }}>
                Reihenfolge {sortConfig.key === "sort_order" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("rec_date")} style={{ cursor: "pointer" }}>
                Erstelldatum {sortConfig.key === "rec_date" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-muted">
                  Keine Startguthabenarten vorhanden.
                </td>
              </tr>
            )}
            {currentRows.map((row) => (
              <tr key={row.credit_type_id}>
                <td>{row.credit_type_id}</td>
                <td><strong>{row.credit_type_name}</strong></td>
                <td>{row.credit_type_code || "–"}</td>
                <td>{row.unit_symbol || "–"}</td>
                <td>
                  {row.is_active ? (
                    <Badge bg="success">Aktiv</Badge>
                  ) : (
                    <Badge bg="warning" text="dark">Inaktiv</Badge>
                  )}
                </td>
                <td>{row.sort_order === null || row.sort_order === undefined ? "–" : row.sort_order}</td>
                <td style={{ whiteSpace: "nowrap" }}>{formatDate(row.rec_date)}</td>
                <td>
                  <ButtonGroup>
                    <Button
                      variant="warning"
                      onClick={() => handleEditClick(row)}
                      className="mr-2"
                      title="Bearbeiten"
                      aria-label="Bearbeiten"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="danger"
                      onClick={(e) => handleDeleteClick(row, e)}
                      title="Löschen"
                      aria-label="Löschen"
                    >
                      <FaTrash />
                    </Button>
                  </ButtonGroup>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <Pagination>
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />
          {[...Array(totalPages)].map((_, index) => (
            <Pagination.Item
              key={index}
              active={index + 1 === currentPage}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
          <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
        </Pagination>
      )}

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Startguthabenart erstellen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateSubmit}>
            {renderFormFields()}
            <Button variant="primary" type="submit" className="mt-3" disabled={loading}>
              {loading ? "Wird gespeichert..." : "Startguthabenart speichern"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Startguthabenart bearbeiten</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            {renderFormFields()}
            <Button variant="primary" type="submit" className="mt-3" disabled={loading}>
              {loading ? "Wird aktualisiert..." : "Startguthabenart aktualisieren"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default BoardStartCreditTypes;
